import React, { useState, useEffect, useMemo } from 'react';
import ListWithDetails from 'components/Common/ListWithDetails';
import GenericBadge from 'components/Utils/GenericBadge';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import PerformanceUtils from 'services/PerformanceUtils';
import HookDetails from '../HookDetails';
import './style.scss';
import { EventSchema, HookConfiguration } from '../../../generated/api';

const HooksManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const [hooks, setHooks] = useState<HookConfiguration[]>([]);
  const [eventSchemas, setEventSchemas] = useState<EventSchema[]>([]);
  const [filter, setFilter] = useState<string>('');
  const [initialSelection, setInitialSelection] = useState<string | null>(null);
  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);

  // Load hooks and event schemas on component mount
  useEffect(() => {
    loadHooks();
    loadEventSchemas();
  }, [dispatch]);

  async function loadHooks(): Promise<void> {
    try {
      const response = await dispatch(api.Hooks.getHooks({}));
      console.log(response);
      if (response) {
        setHooks(response);
      }
    } catch (error) {
      console.error('Failed to load hooks:', error);
      // Start with empty array if API fails
      setHooks([]);
    }
  }

  async function loadEventSchemas(): Promise<void> {
    try {
      const response = await dispatch(api.Hooks.getEventSchemas());
      if (response) {
        setEventSchemas(response as EventSchema[]);
      }
    } catch (error) {
      console.error('Failed to load event schemas:', error);
      setEventSchemas([]);
    }
  }

  function onAddNewHook(): void {
    // Enable create mode and clear any existing selection
    setIsCreateMode(true);
    setInitialSelection(null);
  }

  function onHookResult(hook: HookConfiguration): void {
    if (hook) {
      if (isCreateMode) {
        // Add newly created hook to the list and exit create mode
        setHooks((prevState) => [hook, ...prevState]);
        setIsCreateMode(false);
        setInitialSelection(hook.id!); // Select the newly created hook
      } else {
        // Update existing hook in the list
        setHooks((prevState) => [...PerformanceUtils.editOrAdd(hook, prevState)]);
      }
    }
  }

  function onHookDelete(hookId: string): void {
    setHooks((prevState) => prevState.filter((h) => h.id !== hookId));
  }

  function onHookCancel(hook: HookConfiguration): void {
    if (isCreateMode) {
      // Exit create mode and optionally select the first hook
      setIsCreateMode(false);
      setInitialSelection(hooks[0]?.id || null);
    }
    // For edit mode, no action needed (form reverts automatically)
  }

  // Group hooks by category
  const groupedHooks = useMemo(() => {
    const filtered = filter
      ? hooks.filter(
          (hook) =>
            hook.name?.toLowerCase().includes(filter.toLowerCase()) ||
            hook.event_type.toLowerCase().includes(filter.toLowerCase()),
        )
      : hooks;

    // Create a map of category -> hooks
    const groups: { [category: string]: HookConfiguration[] } = {};

    filtered.forEach((hook) => {
      const hookEvent = eventSchemas.find((schema) => schema.event_type === hook.event_type);
      const category = hookEvent?.category || 'Other';

      if (!groups[category]) {
        groups[category] = [];
      }
      //@ts-ignore
      groups[category].push(hook);
    });

    // Sort hooks within each category by name
    Object.keys(groups).forEach((category) => {
      //@ts-ignore
      groups[category].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });

    return groups;
  }, [hooks, filter, eventSchemas]);

  // Flatten grouped hooks for the list (with category markers)
  const filteredHooks = useMemo(() => {
    const flatList: (HookConfiguration & { isCategory?: boolean; categoryName?: string })[] = [];
    const sortedCategories = Object.keys(groupedHooks).sort();

    sortedCategories.forEach((category) => {
      // Add category header marker
      flatList.push({
        id: `category-${category}`,
        name: category,
        event_type: '',
        enabled: false,
        actions: [],
        isCategory: true,
        categoryName: category,
      } as any);

      //@ts-ignore
      flatList.push(...groupedHooks[category]);
    });

    return flatList;
  }, [groupedHooks]);

  const renderHookBadge = (
    hook: HookConfiguration & { isCategory?: boolean; categoryName?: string },
  ) => {
    // Render category header
    if (hook.isCategory) {
      return (
        <div key={hook.id} className="hook-category-header">
          <span className="hook-category-title">{hook.categoryName}</span>
        </div>
      );
    }

    // Render regular hook badge
    const hookEvent = eventSchemas.find((schema) => schema.event_type === hook.event_type);
    return (
      <GenericBadge
        key={hook.id || 'new'}
        title={hook.name || 'New Hook'}
        info={
          <div>
            <span>{hookEvent?.name || hook.event_type || 'Select event type'}</span>
            <br />
            <span className={`hook-status ${hook.enabled ? 'enabled' : 'disabled'}`}>
              {hook.enabled ? '✓ Enabled' : '✗ Disabled'} • {hook.actions?.length || 0} actions
            </span>
          </div>
        }
      />
    );
  };

  const renderHookDetails = (hook: HookConfiguration | null) => {
    // Don't render details for category headers
    if (hook && (hook as any).isCategory) {
      return null;
    }

    return (
      <HookDetails
        key={isCreateMode ? 'create-mode' : hook?.id || 'no-selection'}
        hook={isCreateMode ? null : hook}
        eventSchemas={eventSchemas}
        onSave={onHookResult}
        onDelete={onHookDelete}
        onCancel={onHookCancel}
      />
    );
  };

  const handleHookClick = (hook: HookConfiguration & { isCategory?: boolean }) => {
    // Prevent selecting category headers
    if (hook.isCategory) {
      return;
    }
  };

  return (
    <div className="hooks-management box full-width full-height">
      <ListWithDetails
        items={filteredHooks}
        idField="id"
        initialItem={initialSelection as any}
        onClick={handleHookClick}
        overrideDetails={isCreateMode ? renderHookDetails(null) : undefined}
        addButton={
          <div className="hooks-controls">
            <DefaultButton
              name={isCreateMode ? 'Creating Hook...' : 'Add Hook'}
              icon="plus"
              onClick={onAddNewHook}
              disabled={isCreateMode}
              style={{ display: 'block', margin: 'auto', marginBottom: '16px', width: '80%' }}
            />
            <div style={{ margin: '0 20px' }}>
              <DefaultTextField
                label="Filter hooks..."
                style={{ width: '100%' }}
                name="filter"
                value={filter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setFilter(event.target.value)
                }
                placeholder="Search by name or event type"
              />
            </div>
          </div>
        }
        renderBadge={renderHookBadge}
        renderDetails={renderHookDetails}
      />
    </div>
  );
};

export default HooksManagement;
